// IMPORTAÇÃO DAS BIBLIOTECAS----------------------------------------------------------------------
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MdClose } from 'react-icons/md';
import styles from "./newsPanel.module.css";

// IMPORTAÇÃO DOS COMPONENTES ---------------------------------------------------------------------
import { useAppContext } from "../../../AppContext.js";

const NewsPanel = ({ onCancel }) => {
    const { t } = useTranslation();
    const { isApp } = useAppContext(); // define se está sendo acessado pelo app ou pelo site
    const [currentTip, setCurrentTip] = useState(0);

    // Estados para controle de arrasto (swipe)
    const [dragStartX, setDragStartX] = useState(null);
    const [dragEndX, setDragEndX] = useState(null);

    // Gerencia efeitos colaterais do modal (scroll lock, fechar com ESC).
    // useEffect(() => {
    //     document.body.style.overflow = "hidden";
    //     const handleEsc = (e) => { if (e.key === "Escape") onCancel(); };
    //     window.addEventListener("keydown", handleEsc);
    //     return () => {
    //         document.body.style.overflow = "";
    //         window.removeEventListener("keydown", handleEsc);
    //     };
    // }, [onCancel]);

    const tips = [
        {
            title: t('usageTips.newsTitle', 'We have news!'),
            desc: t('usageTips.newsDesc', 'We have prepared amazing new features to make your organization easier. Check out whats new in the following tips!'),
            anim: (
                <svg viewBox="0 0 120 120" className={styles.svgAnim}>
                    <defs>
                        <linearGradient id="gradNews" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#2575fc" />
                            <stop offset="100%" stopColor="#1565c0" />
                        </linearGradient>
                    </defs>

                    {/* Brilhos de fundo */}
                    <g opacity="0.8">
                        <path d="M60 15 L63 25 L73 28 L63 31 L60 41 L57 31 L47 28 L57 25 Z" fill="#ffd700">
                            <animate attributeName="opacity" values="0;1;0" dur="2s" repeatCount="indefinite" />
                        </path>
                        <path d="M25 45 L27 50 L32 52 L27 54 L25 59 L23 54 L18 52 L23 50 Z" fill="#ffd700">
                            <animate attributeName="opacity" values="0;1;0" dur="2.5s" repeatCount="indefinite" begin="0.5s" />
                        </path>
                    </g>

                    {/* Presente Animado */}
                    <g transform="translate(60 75)">
                        <g>
                            <animateTransform attributeName="transform" type="scale" values="0.8;1.05;1" dur="0.8s" fill="freeze" />
                            <rect x="-20" y="-15" width="40" height="30" rx="3" fill="url(#gradNews)" />
                            <rect x="-22" y="-22" width="44" height="8" rx="2" fill="#1565c0" />
                            <rect x="-4" y="-22" width="8" height="30" fill="#ffd700" />
                            <path d="M0 -22 Q-10 -35 -15 -22 Q-10 -28 0 -22 Q10 -35 15 -22 Q10 -28 0 -22" fill="#ffd700" />
                        </g>
                        <animateTransform attributeName="transform" type="rotate" values="-2;2;-2" dur="2s" repeatCount="indefinite" additive="sum" />
                    </g>

                    {/* Confetes */}
                    <circle cx="20" cy="-10" r="3" fill="#ff4081">
                        <animate attributeName="cy" from="-10" to="130" dur="3s" repeatCount="indefinite" />
                    </circle>
                    <rect x="95" y="-20" width="5" height="5" fill="#00e5ff">
                        <animate attributeName="cy" from="-20" to="130" dur="2.5s" repeatCount="indefinite" />
                        <animateTransform attributeName="transform" type="rotate" from="0" to="360" dur="2s" repeatCount="indefinite" />
                    </rect>
                </svg>
            )
        },
        {
            title: t('usageTips.listTitle', 'Task Lists'),
            desc: t('usageTips.listDesc', 'Now you can add lists to your notes. Check and uncheck list items easily!'),
            anim: (
                <svg viewBox="0 0 120 120" className={styles.svgAnim}>
                    <rect x="20" y="25" width="80" height="70" rx="10" fill="white" stroke="#e0e6ed" strokeWidth="1" />

                    <g transform="translate(35, 45)">
                        <rect x="0" y="0" width="10" height="10" rx="2" fill="#2575fc" />
                        <path d="M2 5 L4 7 L8 3" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                        <rect x="18" y="3" width="40" height="4" rx="2" fill="#dcdde1" />
                        <line x1="18" y1="5" x2="58" y2="5" stroke="#636e72" strokeWidth="1" opacity="0.5" />
                    </g>

                    <g transform="translate(35, 65)">
                        <rect x="0" y="0" width="10" height="10" rx="2" fill="none" stroke="#2575fc" strokeWidth="1.5">
                            <animate attributeName="fill" values="none;#2575fc;none" dur="2s" repeatCount="indefinite" />
                        </rect>
                        <path d="M2 5 L4 7 L8 3" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round" opacity="0">
                            <animate attributeName="opacity" values="0;1;0" dur="2s" repeatCount="indefinite" />
                        </path>
                        <rect x="18" y="3" width="35" height="4" rx="2" fill="#636e72" />
                    </g>
                </svg>
            )
        },
        {
            title: t('usageTips.audioTitle', 'Quick Voice Notes'),
            desc: t('usageTips.audioDesc', 'Hold down the add note button to record audio. It will be automatically transcribed into a new text note!'),
            anim: (
                <svg viewBox="0 0 120 120" className={styles.svgAnim}>
                    <defs>
                        <linearGradient id="gradMic" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#2575fc" />
                            <stop offset="100%" stopColor="#6a11cb" />
                        </linearGradient>
                    </defs>

                    {/* Ondas de som */}
                    <g className={styles.soundWaves}>
                        <circle cx="60" cy="60" r="10" fill="none" stroke="#2575fc" strokeWidth="2" opacity="0.8" />
                        <circle cx="60" cy="60" r="22" fill="none" stroke="#2575fc" strokeWidth="2" opacity="0.4" />
                        <circle cx="60" cy="60" r="34" fill="none" stroke="#2575fc" strokeWidth="2" opacity="0.2" />
                    </g>

                    {/* Microfone */}
                    <g className={styles.pulseBtn}>
                        <rect x="51" y="45" width="18" height="30" rx="9" fill="url(#gradMic)" />
                        <path d="M42 60a18 18 0 0 0 36 0M60 78v10" fill="none" stroke="#2575fc" strokeWidth="3" strokeLinecap="round" />
                    </g>
                </svg>
            )
        },
        {
            title: t('usageTips.fullscreenTitle', 'Full Focus Mode'),
            desc: isApp
                ? t('usageTips.fullscreenDescApp', 'Double-tap the title of any note to expand it and focus only on the content.')
                : t('usageTips.fullscreenDescWeb', 'Double-click the title of any note to open it in full-screen mode and edit easily.'),
            anim: (
                <svg viewBox="0 0 120 120" className={styles.svgAnim}>
                    {/* Fundo que representa a interface do app (lista de notas) */}
                    <rect x="10" y="20" width="100" height="80" rx="4" fill="#f0f2f5" stroke="#e0e6ed" strokeWidth="1" />
                    <rect x="15" y="30" width="90" height="10" rx="2" fill="#fff" opacity="0.6" />
                    <rect x="15" y="80" width="90" height="10" rx="2" fill="#fff" opacity="0.6" />

                    {/* Nota central que sofre o clique e expande */}
                    <g className={styles.noteFocusGroup}>
                        <rect x="25" y="45" width="70" height="30" rx="3" fill="#fff" stroke="#ccc" strokeWidth="0.5" />
                        {/* Título da nota */}
                        <rect x="30" y="50" width="25" height="3" rx="1.5" fill="#2575fc" />
                        {/* Conteúdo simulado */}
                        <rect x="30" y="58" width="55" height="1.5" rx="0.75" fill="#eee" />
                        <rect x="30" y="64" width="55" height="1.5" rx="0.75" fill="#eee" />
                        <rect x="30" y="70" width="40" height="1.5" rx="0.75" fill="#eee" />

                        {/* Moldura de "Tela Cheia" que surge na expansão */}
                        <rect x="0" y="0" width="120" height="120" fill="none" stroke="#2575fc" strokeWidth="0" className={styles.fullScreenBorder} />
                    </g>

                    {/* Feedback de clique duplo no título */}
                    <g className={styles.tapFeedback}>
                        <circle cx="48" cy="50" r="5" fill="#2575fc" className={styles.tap1} />
                        <circle cx="48" cy="50" r="5" fill="#2575fc" className={styles.tap2} />
                    </g>
                </svg>
            )
        },
        {
            title: t('usageTips.upcomingTitle', 'More news to come!'),
            desc: t('usageTips.upcomingDesc', 'Get ready: soon youll be able to attach photos, audios, and documents to your notes. Stay tuned for the next update!'),
            anim: (
                <svg viewBox="0 0 120 120" className={styles.svgAnim}>
                    <defs>
                        <linearGradient id="gradUpcoming" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#2575fc" />
                            <stop offset="100%" stopColor="#6a11cb" />
                        </linearGradient>
                    </defs>

                    {/* Partículas festivas de fundo */}
                    <g opacity="0.6">
                        <path d="M20 20 L23 24 L27 22 L24 18 Z" fill="#ffd700">
                            <animate attributeName="opacity" values="0;1;0" dur="2s" repeatCount="indefinite" />
                            <animateTransform attributeName="transform" type="rotate" from="0 23 21" to="360 23 21" dur="4s" repeatCount="indefinite" />
                        </path>
                        <circle cx="100" cy="30" r="2" fill="#ff4081">
                            <animate attributeName="opacity" values="0;1;0" dur="2.5s" begin="0.5s" repeatCount="indefinite" />
                        </circle>
                        <rect x="60" y="100" width="4" height="4" fill="#00e5ff" transform="rotate(45 62 102)">
                            <animate attributeName="opacity" values="0;1;0" dur="3s" begin="1s" repeatCount="indefinite" />
                        </rect>
                    </g>

                    {/* Ícones flutuantes dos futuros recursos */}
                    {/* Ícone de Foto */}
                    <g transform="translate(25, 45)">
                        <animateTransform attributeName="transform" type="translate" values="25 45; 25 38; 25 45" dur="3s" repeatCount="indefinite" />
                        <rect width="18" height="14" rx="3" fill="#ff4081" />
                        <circle cx="6" cy="5" r="2" fill="white" opacity="0.5">
                            <animate attributeName="opacity" values="0.4;1;0.4" dur="1.5s" repeatCount="indefinite" />
                        </circle>
                        <rect x="10" y="2" width="5" height="2" fill="white" opacity="0.3" />
                    </g>

                    {/* Ícone de Áudio */}
                    <g transform="translate(53, 25)">
                        <animateTransform attributeName="transform" type="translate" values="53 25; 53 32; 53 25" dur="3.5s" repeatCount="indefinite" />
                        <rect width="14" height="22" rx="7" fill="#2575fc" />
                        <path d="M-4 12 a11 11 0 0 0 22 0" fill="none" stroke="#2575fc" strokeWidth="2" strokeLinecap="round">
                            <animate attributeName="stroke-width" values="2;3;2" dur="1.2s" repeatCount="indefinite" />
                        </path>
                    </g>

                    {/* Ícone de Documento */}
                    <g transform="translate(78, 48)">
                        <animateTransform attributeName="transform" type="translate" values="78 48; 78 41; 78 48" dur="3.2s" repeatCount="indefinite" />
                        <rect width="16" height="20" rx="2" fill="#76ff03" />
                        <line x1="4" y1="6" x2="12" y2="6" stroke="white" strokeWidth="2">
                            <animate attributeName="x2" values="4;12;4" dur="2s" repeatCount="indefinite" />
                        </line>
                        <line x1="4" y1="11" x2="12" y2="11" stroke="white" strokeWidth="2">
                            <animate attributeName="x2" values="4;12;4" dur="2s" begin="0.5s" repeatCount="indefinite" />
                        </line>
                    </g>
                </svg>
            )
        }
    ];

    // Funções para navegação entre os slides
    const nextSlide = () => {
        if (currentTip < tips.length - 1) setCurrentTip(currentTip + 1);
    };

    const prevSlide = () => {
        if (currentTip > 0) setCurrentTip(currentTip - 1);
    };

    const handleNext = () => {
        if (currentTip < tips.length - 1) nextSlide();
        else onCancel();
    };

    // Handlers para eventos de mouse/toque (swipe)
    const handleMouseDown = (e) => {
        setDragStartX(e.clientX);
    };

    const handleMouseMove = (e) => {
        if (dragStartX !== null) {
            setDragEndX(e.clientX);
        }
    };

    const handleMouseUp = () => {
        if (dragStartX !== null && dragEndX !== null) {
            const diff = dragStartX - dragEndX;
            if (diff > 50) nextSlide();
            else if (diff < -50) prevSlide();
        }
        setDragStartX(null);
        setDragEndX(null);
    };

    const handleTouchStart = (e) => {
        setDragStartX(e.touches[0].clientX);
    };

    const handleTouchMove = (e) => {
        if (dragStartX !== null) {
            setDragEndX(e.touches[0].clientX);
        }
    };

    const handleTouchEnd = () => {
        if (dragStartX !== null && dragEndX !== null) {
            const diff = dragStartX - dragEndX;
            if (diff > 50) nextSlide();
            else if (diff < -50) prevSlide();
        }
        setDragStartX(null);
        setDragEndX(null);
    };

    // HTML DO MODAL
    return (
        <div className={styles.overlay}>
            <div
                className={styles.modal}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                <button className={styles.closeBtn} onClick={onCancel} aria-label={t("Close")}>
                    <MdClose size={20} />
                </button>

                <div className={styles.content}>
                    <div className={styles.animationArea}>
                        {tips[currentTip].anim}
                    </div>

                    <h2 className={styles.title}>{tips[currentTip].title}</h2>
                    <p className={styles.description}>{tips[currentTip].desc}</p>
                </div>

                <div className={styles.footer}>
                    <div className={styles.dots}>
                        {tips.map((_, i) => (
                            <span
                                key={i}
                                className={`${styles.dot} ${currentTip === i ? styles.activeDot : ''}`}
                                onClick={() => setCurrentTip(i)}
                            />
                        ))}
                    </div>
                    <button className={styles.actionBtn} onClick={handleNext}>
                        {currentTip === tips.length - 1 ? t("Finish", "Entendi!") : t("Next", "Próximo")}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default NewsPanel;
